import { Box, Button, Typography } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import SouthIcon from "@mui/icons-material/South";
import type { SponsorCampaign } from "../campaignTypes";
import { PORTAL_MAX_WIDTH, bodySx, portalFont } from "../partnerTheme";
import FeaturedMetric from "./FeaturedMetric";

export default function CampaignHero({
  campaign,
  points,
}: {
  campaign: SponsorCampaign;
  points?: readonly string[];
}) {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        minHeight: { xs: "auto", md: "92vh" },
        display: "flex",
        alignItems: "stretch",
        isolation: "isolate",
        pb: { xs: 4, md: 5 },
      }}
    >
      <Box
        component="img"
        src={campaign.heroImage}
        alt={campaign.heroImageAlt}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: { xs: "center top", md: "70% center" },
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          background: {
            xs: "linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.72) 45%, rgba(10,10,10,0.96) 100%)",
            md: "linear-gradient(90deg, rgba(10,10,10,0.96) 0%, rgba(10,10,10,0.82) 38%, rgba(10,10,10,0.35) 68%, rgba(10,10,10,0.55) 100%), linear-gradient(180deg, transparent 55%, rgba(10,10,10,0.92) 100%)",
          },
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: PORTAL_MAX_WIDTH,
          width: "100%",
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          pt: { xs: 4.5, md: 10 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: { xs: 3, md: 4 },
          minHeight: { md: "calc(92vh - 72px)" },
        }}
      >
        <Box sx={{ maxWidth: "100%", width: "100%" }}>
          <Box
            sx={{
              display: { xs: "block", md: "inline-block" },
              maxWidth: "100%",
              width: { xs: "100%", md: "auto" },
              verticalAlign: "top",
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontSize: { xs: "0.6875rem", md: "0.75rem" },
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--portal-accent)",
                mb: { xs: 1.5, md: 2 },
              }}
            >
              {campaign.eyebrow}
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: "clamp(1.7rem, 7.2vw + 0.2rem, 3.75rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.03em",
                mb: { xs: 2, md: 2.5 },
              }}
            >
              <Box
                component="span"
                sx={{ display: "block", whiteSpace: { xs: "normal", md: "nowrap" } }}
              >
                {campaign.headline}
              </Box>
              {campaign.headlineLine2 ? (
                <Box component="span" sx={{ display: "block" }}>
                  {campaign.headlineLine2}
                </Box>
              ) : null}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 1.25, sm: 2.5 },
                mb: { xs: 2, md: 2.5 },
              }}
            >
              <Meta
                icon={<LocationOnOutlinedIcon sx={{ fontSize: 16 }} />}
                text={campaign.venue}
              />
              <Meta
                icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 15 }} />}
                text={campaign.dateRange}
              />
            </Box>

            <Typography
              sx={{
                ...bodySx,
                color: "rgba(244,245,247,0.88)",
                // On desktop, lock copy to the Soap title line width.
                width: { xs: "100%", md: 0 },
                minWidth: { xs: 0, md: "100%" },
                mb: 2.5,
                fontSize: { xs: "0.9375rem", md: "1.0625rem" },
              }}
            >
              {campaign.campaignSummary}
            </Typography>
            {points && points.length > 0 ? (
              <Box component="ul" sx={{ m: 0, mb: 2.5, pl: 2.25, maxWidth: 560 }}>
                {points.map((point) => (
                  <Typography
                    key={point}
                    component="li"
                    sx={{ fontFamily: portalFont, color: "#fff", fontSize: "0.98rem", lineHeight: 1.45, mb: 0.6 }}
                  >
                    {point}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </Box>

          <Box sx={{ mb: 3, width: { xs: "100%", sm: "auto" }, maxWidth: { xs: "100%", sm: 420 } }}>
            <FeaturedMetric metric={campaign.featuredMetric} />
          </Box>

          <Button
            href={campaign.heroCtaHref}
            endIcon={<SouthIcon sx={{ fontSize: 16 }} />}
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: "var(--portal-accent)",
              color: "#fff",
              borderRadius: "999px",
              px: 3,
              py: { xs: 1.5, md: 1.35 },
              fontSize: "0.9375rem",
              width: { xs: "100%", sm: "auto" },
              "&:hover": { bgcolor: "var(--portal-accent)", filter: "brightness(1.12)" },
              "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 3 },
            }}
          >
            {campaign.heroCtaLabel}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function Meta({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box sx={{ display: "flex", color: "var(--portal-text-muted)" }}>{icon}</Box>
      <Typography
        sx={{ fontFamily: portalFont, fontSize: "0.875rem", color: "var(--portal-text-muted)" }}
      >
        {text}
      </Typography>
    </Box>
  );
}
